import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { BarChart3, ShieldCheck } from 'lucide-react';
import { transparencyService } from '../services';
import { LoadingPage } from '../components/ui/Loading';
import Select from '../components/ui/Select';
import SummaryCards from '../components/transparency/SummaryCards';
import SplitBar from '../components/transparency/SplitBar';
import ExpensesTable from '../components/transparency/ExpensesTable';
import DistributionTable from '../components/transparency/DistributionTable';
import HistoryList from '../components/transparency/HistoryList';
import { TABS, latestOnly } from '../utils/transparency';

const PANEL_CLASS = 'bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-xl rounded-2xl p-6 border border-white/10 shadow-xl';
const PREVIEW_LIMIT = 5;

const TransparencyPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('summary');

  // Só a resposta da chamada mais recente é aplicada (cliques rápidos no histórico).
  const fetchLatest = useMemo(() => latestOnly(transparencyService.get), []);

  const load = useCallback(async (bcPaymentId) => {
    setLoading(true);
    setError('');
    try {
      const { stale, value } = await fetchLatest(bcPaymentId);
      if (stale) return;
      setData(value);
      setLoading(false);
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao carregar a transparência.');
      setLoading(false);
    }
  }, [fetchLatest]);

  useEffect(() => {
    load();
  }, [load]);

  // Clicar num BC do histórico abre o resumo dele; trocar no seletor mantém a aba atual.
  const handleHistorySelect = (bcPaymentId) => {
    setActiveTab('summary');
    load(bcPaymentId);
  };

  if (loading && !data) return <LoadingPage />;

  const treasurers = data?.treasurers?.length ? data.treasurers.join(', ') : '—';

  return (
    <div className="space-y-6">
      <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl sm:text-4xl font-bold bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent flex items-center gap-3">
            Transparência Bless Castle <BarChart3 className="w-8 h-8 text-cyan-400" />
          </h1>
          <p className="text-gray-300 mt-2">
            Prestação de contas clara e atualizada para todos os membros do clã.
          </p>
        </div>
        <div className={`${PANEL_CLASS} flex items-center gap-4 lg:min-w-[280px]`}>
          <div className="p-3 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600">
            <ShieldCheck className="w-6 h-6 text-white" />
          </div>
          <div>
            <p className="text-gray-400 text-sm">Tesoureiro Responsável</p>
            <p className="text-white text-xl font-bold">{treasurers}</p>
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/50 rounded-xl p-4">
          <p className="text-red-400">{error}</p>
        </div>
      )}

      {data && data.selected === null ? (
        <div className={PANEL_CLASS}>
          <p className="text-gray-300">Nenhum pagamento BC registrado ainda.</p>
        </div>
      ) : data && (
        <>
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-white/10 pb-2">
            <div className="flex gap-2 overflow-x-auto">
              {TABS.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setActiveTab(tab.id)}
                  className={`whitespace-nowrap px-4 py-2 rounded-xl transition-all ${
                    activeTab === tab.id
                      ? 'bg-cyan-500/10 text-white border border-cyan-500/60 shadow-lg shadow-cyan-500/20'
                      : 'text-gray-300 hover:text-white hover:bg-white/5'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
            <div className="w-full md:w-64">
              <Select value={data.selected} onChange={(e) => load(e.target.value)} disabled={loading}>
                <option value="all">Todos os BCs</option>
                {data.options.map((option) => (
                  <option key={option.id} value={option.id}>{option.name}</option>
                ))}
              </Select>
            </div>
          </div>

          {activeTab === 'summary' && (
            <div className="space-y-6">
              <SummaryCards summary={data.summary} />
              <SplitBar summary={data.summary} />
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className={`${PANEL_CLASS} space-y-4`}>
                  <div className="flex items-center justify-between">
                    <h2 className="text-xl font-bold text-white">Despesas do Clã</h2>
                    <button type="button" className="text-cyan-400 text-sm hover:underline" onClick={() => setActiveTab('expenses')}>
                      ver tudo
                    </button>
                  </div>
                  <ExpensesTable expenses={data.expenses} limit={PREVIEW_LIMIT} />
                </div>
                <div className={`${PANEL_CLASS} space-y-4`}>
                  <div className="flex items-center justify-between">
                    <h2 className="text-xl font-bold text-white">Distribuição dos Players</h2>
                    <button type="button" className="text-cyan-400 text-sm hover:underline" onClick={() => setActiveTab('distribution')}>
                      ver tudo
                    </button>
                  </div>
                  <DistributionTable distribution={data.distribution} limit={PREVIEW_LIMIT} />
                </div>
              </div>
            </div>
          )}

          {activeTab === 'expenses' && (
            <div className={`${PANEL_CLASS} space-y-4`}>
              <div>
                <h2 className="text-xl font-bold text-white">Despesas do Clã</h2>
                <p className="text-gray-400 text-sm">Saídas do caixa administrativo nos últimos 30 dias.</p>
              </div>
              <ExpensesTable expenses={data.expenses} />
            </div>
          )}

          {activeTab === 'distribution' && (
            <div className={`${PANEL_CLASS} space-y-4`}>
              <div>
                <h2 className="text-xl font-bold text-white">Distribuição dos Players</h2>
                <p className="text-gray-400 text-sm">
                  {data.distribution.mode === 'aggregate'
                    ? 'Total por beneficiário em todos os BCs.'
                    : 'Cota de cada beneficiário neste BC.'}
                </p>
              </div>
              <DistributionTable distribution={data.distribution} />
            </div>
          )}

          {activeTab === 'history' && (
            <div className={`${PANEL_CLASS} space-y-4`}>
              <h2 className="text-xl font-bold text-white">Histórico</h2>
              <HistoryList history={data.history} selected={data.selected} onSelect={handleHistorySelect} />
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default TransparencyPage;
